import { inject, Injectable, NgZone } from '@angular/core';
import { ColDef } from 'ag-grid-enterprise';
import { OFFICE_CODES } from '../../shared/constants/office-codes.constants';
import { IOffice } from '../../shared/interfaces/office.interface';
import { IClaim, IGetClaimsRequest } from '../../shared/interfaces/claims.model';
import { ExpandableCellComponent } from '../../features/claims/expandable-cell/expandable-cell';
import { BehaviorSubject, take } from 'rxjs';
import { TableFieldNames, TableStaticColumns } from '../../shared/enums/table-column.enum';
import { ClaimStore } from '../store/claim-store';
import { DatePipe } from '@angular/common';
import { DialogDataMapperService } from '../../shared/services/dialog-data-mapper.service';
import { IClaimsDialogData } from '../../shared/interfaces/claims-dialog.model';
import { MatDialog } from '@angular/material/dialog';
import { AddEditDialogComponent } from '../../shared/components/add-edit-claim/add-edit-dialog.component';
import { CRM_LINK_TEMPLATE, ASSERTIONS_GRID_COLUMN_DEFS } from '../../shared/constants/claims-ui-constants';

@Injectable({
  providedIn: 'root',
})
export class GridService {
  public numberOfLines = new BehaviorSubject<number>(0);
  private readonly store = inject(ClaimStore);
  private readonly datePipe = inject(DatePipe);
  private readonly dialog = inject(MatDialog);
  private readonly dataMapperService = inject(DialogDataMapperService);
  public getClaimsGridHeaders(claimData: Array<IClaim>): Array<ColDef> {
    if (claimData.length === 0) {
      return [];
    }

    const claimsHeaders: Array<ColDef> = [
      {
        headerCheckboxSelection: true,
        checkboxSelection: true,
        width: 50,
        pinned: 'left',
        suppressNavigable: true,
        filter: false,
        suppressMenu: true,
      },
      {
        field: TableFieldNames.client,
        sortable: true,
        resizable: true,
        filter: false,
        suppressMenu: true,
      },
      {
        headerName: TableStaticColumns.crmAccount,
        field: TableFieldNames.sfAccount,
        sortable: true,
        resizable: true,
        filter: false,
        suppressMenu: true,
        cellRenderer: (params: any) => {
          const id = params.value;
          if (!id) return '';
          return CRM_LINK_TEMPLATE(id);
        }
      },
      {
        field: TableFieldNames.oppurtunityOwner,
        sortable: true,
        resizable: true,
        filter: false,
        valueGetter: (params: any) =>
          params?.data
            ? params.data.opportunityOwnerName || params.data.opportunityOwner || ''
            : '',
        suppressMenu: true,
      },
      {
        field: TableFieldNames.office,
        sortable: true,
        resizable: true,
        filter: false,
        suppressMenu: true,
        valueFormatter: (params) => {
          const office = OFFICE_CODES.find((o: IOffice) => o.offCode === params.data.officeCode);
          return office ? office.offName : '';
        },
      },
      {
        headerName: TableStaticColumns.view,
        field: '',
        width: 70,
        cellClass: 'e-view-icon-cell',
        cellRenderer: () =>
          `<span attr.aria-label="Edit claim" role="button" class="fad fa-solid fa-user-magnifying-glass"></span>`,
        filter: false,
        suppressMenu: true,
        onCellClicked: (event: any) => {
          this.openEditClaimDialog(event);
        },
      },
      {
        field: TableFieldNames.expiryDate,
        sortable: true,
        resizable: true,
        filter: false,
        suppressMenu: true,
        valueFormatter: (params: any) => this.datePipe.transform(params.value, 'dd/MM/yyyy') ?? '',
        cellClassRules: {
          'e-expired-claim-cell': (params: any) => {
            if (!params.value) { return false; }
            const expiry = new Date(params.value);
            return !isNaN(expiry.getTime()) && expiry.setHours(0, 0, 0, 0) < new Date().setHours(0, 0, 0, 0);
          }
        }
      },
      {
        headerName: TableStaticColumns.wording,
        field: TableFieldNames.wording,
        cellRenderer: ExpandableCellComponent,
        sortable: true,
        width: 300,
        autoHeight: true,
        resizable: true,
        wrapText: true,
        cellClass: 'cell-wrap-text',
        filter: false,
        suppressMenu: true,
      },
    ];

    return claimsHeaders;
  }

  public getClaimAssertionHeaders(): Array<ColDef> {
    return ASSERTIONS_GRID_COLUMN_DEFS;
  }

  public buildClaimsRequest(requireAllClaims: boolean = false): IGetClaimsRequest {
    let selectedgeographyIds: Array<string> | null = null;
    let expiryDate: string | null = null;
    if (!requireAllClaims) {
      const geographies = this.store.selectedGeographies();
      if (geographies != null) {
        const ids = geographies.filter((g) => g.isSelected).map((g) => g.geographyId);

        selectedgeographyIds = ids.length > 0 ? ids : null;
      }
      const selectedExpiry = this.store.selectedExpiryDate();
      if (selectedExpiry != null) {
        expiryDate = this.datePipe.transform(selectedExpiry, 'yyyy-MM-dd') ?? null;
      }
    }
    return {
      geographyIds: selectedgeographyIds,
      expiryDate: expiryDate,
    };
  }

  private openEditClaimDialog(event: any): void {
    const mappedClaimData = this.dataMapperService.mapApiClaimToDialogData(event.data);
    const dialogData: IClaimsDialogData = {
      mode: 'view',
      claimData: mappedClaimData,
    };
    this.dialog.open(AddEditDialogComponent, {
      data: dialogData,
    });
  }
}
